import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';

export interface BulkOutput {
  /** Bytes / blob to save */
  blob: Blob;
  /** Filename to save as (used as ZIP entry name when bulk) */
  filename: string;
}

export interface BulkOptions {
  /** Filename for the ZIP when there are multiple files (no extension). */
  zipName?: string;
  /** Toast on success */
  toastSuccess?: boolean;
  /** Optional progress callback (idx, total) — for slow per-file ops */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Run an async per-file processor over a list of files, then either save the
 * single result directly or pack everything into a ZIP. Designed so each
 * single-file image tool can opt into bulk just by switching its dropzone to
 * multiple and replacing its export logic with one call to this helper.
 *
 * Errors from the processor are caught per-file: the file gets skipped (with
 * a toast) and the rest of the batch still completes.
 */
export async function processAndSave(
  files: File[],
  process: (file: File, index: number) => Promise<BulkOutput>,
  opts: BulkOptions = {},
): Promise<{ saved: number; failed: number }> {
  if (files.length === 0) return { saved: 0, failed: 0 };

  // Single-file path — no zip wrapper, original UX preserved.
  if (files.length === 1) {
    try {
      const out = await process(files[0], 0);
      saveAs(out.blob, out.filename);
      if (opts.toastSuccess !== false) toast.success('Saved');
      opts.onProgress?.(1, 1);
      return { saved: 1, failed: 0 };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Bulk processor failed on single file:', e);
      toast.error(`Failed: ${files[0].name}`);
      return { saved: 0, failed: 1 };
    }
  }

  // Bulk path — accumulate into a ZIP.
  const zip = new JSZip();
  const used = new Set<string>();
  let saved = 0;
  let failed = 0;

  for (let i = 0; i < files.length; i++) {
    try {
      const out = await process(files[i], i);
      // Ensure unique names inside the zip.
      let name = out.filename;
      let n = 1;
      while (used.has(name)) {
        const dot = out.filename.lastIndexOf('.');
        name =
          dot > 0
            ? `${out.filename.slice(0, dot)} (${n})${out.filename.slice(dot)}`
            : `${out.filename} (${n})`;
        n++;
      }
      used.add(name);
      const buf = await out.blob.arrayBuffer();
      zip.file(name, buf);
      saved++;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`Bulk processor failed for ${files[i].name}:`, e);
      failed++;
    }
    opts.onProgress?.(i + 1, files.length);
  }

  if (saved === 0) {
    toast.error('All files failed — check the console for details');
    return { saved: 0, failed };
  }

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
  const zipName = `${opts.zipName ?? 'output'}.zip`;
  saveAs(zipBlob, zipName);

  if (opts.toastSuccess !== false) {
    if (failed > 0) {
      toast.success(`Saved ${saved} files · ${failed} failed`);
    } else {
      toast.success(`Saved ${saved} files`);
    }
  }
  return { saved, failed };
}

/** Convenience: turn a canvas into a Blob with a chosen mime/quality. */
export function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime = 'image/png',
  quality?: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas conversion failed'))),
      mime,
      quality,
    );
  });
}

/** Strip extension from a filename. Used when building per-file output names. */
export function stripExt(name: string): string {
  return name.replace(/\.[^./\\]+$/, '');
}
